import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { ChartBar, Tray, QrCode, X, LinkBreak, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../../App';
import OverviewPage from './pages/OverviewPage';
import InboxPage from './pages/InboxPage';
import TaskDetailPage from './pages/TaskDetailPage';
import { disconnectBridge, fetchBridgeQr } from './api/whatsappTasksApi';
import useVisibleInterval from '../../hooks/useVisibleInterval';

const tabs = [
  { path: '', label: 'Overview', icon: ChartBar, end: true },
  { path: 'inbox', label: 'Inbox', icon: Tray },
];

const ADMIN_ROLES = new Set(['super_admin', 'admin']);

function WhatsAppTasksLayout() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrState, setQrState] = useState({ loading: true, connected: false, qr: null, error: null });
  const [disconnecting, setDisconnecting] = useState(false);

  const loadQr = useCallback(async () => {
    try {
      const data = await fetchBridgeQr();
      setQrState({ loading: false, connected: !!data.connected, qr: data.qr || null, error: null });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not reach the WhatsApp bridge.';
      setQrState({ loading: false, connected: false, qr: null, error: msg });
    }
  }, []);

  useEffect(() => {
    loadQr();
  }, [loadQr]);

  useEffect(() => {
    if (!showQrModal) return;
    setQrState((s) => ({ ...s, loading: true, error: null }));
    loadQr();
  }, [showQrModal, loadQr]);

  useVisibleInterval(loadQr, 20000, showQrModal);

  const handleDisconnect = async () => {
    const confirmed = window.confirm(
      'Disconnect WhatsApp?\n\nMessage capture will stop until you scan a new QR code. Existing tasks are kept.',
    );
    if (!confirmed) return;

    setDisconnecting(true);
    try {
      await disconnectBridge();
      setQrState({ loading: true, connected: false, qr: null, error: null });
      setTimeout(loadQr, 1500);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not disconnect WhatsApp.';
      window.alert(msg);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="app-module-shell">
      <div className="tk-module-header">
        <div className="tk-module-brand">
          <div className="tk-module-mark" aria-hidden="true">
            <Tray size={20} weight="fill" />
          </div>
          <div>
            <h1 className="tk-module-title">WhatsApp Ticket Tracking</h1>
            <p className="tk-module-subtitle">Triage, assign, and track group tickets</p>
          </div>
        </div>
      </div>

      <nav className="tk-subnav" aria-label="WhatsApp tasks navigation">
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path === '' ? '/dashboard/whatsapp-tasks' : `/dashboard/whatsapp-tasks/${tab.path}`}
            end={tab.end}
            className={({ isActive }) => `tk-subnav-link${isActive ? ' is-active' : ''}`}
          >
            <tab.icon size={16} />
            <span>{tab.label}</span>
          </NavLink>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', padding: '6px 4px' }}>
          {!qrState.loading && qrState.connected && (
            <span className="tk-pill tk-pill-status-completed">
              Connected
            </span>
          )}

          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => setShowQrModal(true)}>
            <QrCode size={14} />
            {qrState.connected ? 'WhatsApp' : 'Scan QR'}
          </button>

          {isAdmin && qrState.connected && (
            <button
              type="button"
              className="tk-btn tk-btn-secondary"
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{ borderColor: '#FECACA', color: '#B91C1C', background: '#FEF2F2' }}
            >
              <LinkBreak size={14} />
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          )}

          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => setRefreshKey((k) => k + 1)}>
            Refresh
          </button>
        </div>
      </nav>

      <div className="tk-page">
      <Routes>
        <Route index element={<OverviewPage refreshKey={refreshKey} />} />
        <Route path="inbox" element={<InboxPage refreshKey={refreshKey} />} />
        <Route path="tasks/:taskNo" element={<TaskDetailPage />} />
      </Routes>
      </div>

      {showQrModal && (
        <div
          onClick={() => setShowQrModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', padding: '12px', width: '100%', maxWidth: '420px', borderRadius: '8px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h2 style={{ fontSize: '12px', fontWeight: '600' }}>
                {qrState.connected ? 'WhatsApp Connected' : 'Scan WhatsApp QR Code'}
              </h2>
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X size={22} />
              </button>
            </div>

            {!qrState.connected && (
              <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
                WhatsApp → Settings → Linked Devices → Link a device
              </p>
            )}

            <div
              style={{
                width: '100%',
                minHeight: '340px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px',
                textAlign: 'center',
              }}
            >
              {qrState.loading && <p style={{ color: '#6B7280', fontSize: '12px' }}>Loading…</p>}

              {!qrState.loading && qrState.error && (
                <>
                  <p style={{ color: '#B91C1C', fontSize: '12px', fontWeight: 600 }}>{qrState.error}</p>
                  <p style={{ color: '#6B7280', fontSize: '12px' }}>
                    Make sure the WhatsApp bridge service is running, then try again.
                  </p>
                </>
              )}

              {!qrState.loading && !qrState.error && qrState.connected && (
                <>
                  <CheckCircle size={48} color="#059669" weight="fill" />
                  <p style={{ color: '#065F46', fontSize: '15px', fontWeight: 600 }}>
                    WhatsApp is connected and capturing group messages.
                  </p>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      style={{
                        marginTop: '8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: disconnecting ? 'wait' : 'pointer',
                        border: '1px solid #FECACA',
                        borderRadius: '8px',
                        background: '#FEF2F2',
                        color: '#B91C1C',
                      }}
                    >
                      <LinkBreak size={16} />
                      {disconnecting ? 'Disconnecting…' : 'Disconnect WhatsApp'}
                    </button>
                  )}
                </>
              )}

              {!qrState.loading && !qrState.error && !qrState.connected && qrState.qr && (
                <>
                  <img src={qrState.qr} alt="WhatsApp QR" style={{ width: '260px', height: '260px' }} />
                  <p style={{ color: '#9CA3AF', fontSize: '12px' }}>Refreshes automatically every 20s</p>
                </>
              )}

              {!qrState.loading && !qrState.error && !qrState.connected && !qrState.qr && (
                <p style={{ color: '#6B7280', fontSize: '12px' }}>
                  QR not ready yet — the bridge is starting up. It will appear shortly.
                </p>
              )}
            </div>

            {!qrState.connected && (
              <button
                type="button"
                onClick={loadQr}
                style={{ marginTop: '12px', width: '100%', padding: '10px', fontSize: '12px', cursor: 'pointer', border: '1px solid #E5E7EB', borderRadius: '8px', background: 'white' }}
              >
                Refresh QR
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppTasksLayout;
